import { useCallback, useEffect, useState } from "react";
import { RefreshCw, PlugZap } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { fmtDateTime } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

// Fix Z (2026-10-13): estado del respaldo en Notion, probar la conexion y sincronizar ahora.
export default function NotionCard({ savedDatabaseId }) {
  const { toast } = useToast();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/academy/notion/status").then(({ data }) => setSt(data)).catch(() => setSt(null));
  }, []);
  useEffect(() => { load(); }, [load, savedDatabaseId]);

  const run = async (what) => {
    setBusy(what);
    try {
      const { data } = await api.post(`/academy/notion/${what}`);
      if (what === "test") {
        toast({ title: `Conectado con "${data.title}"`, description: (data.added.length ? `Se agregaron las columnas: ${data.added.join(", ")}.` : "La base ya tenía todas las columnas.")
          + (data.conflicts.length ? ` No se llenan (ya existen con otro tipo): ${data.conflicts.join(", ")}.` : "") });
      } else {
        // Fix E (2026-10-18): archivos subidos a la columna Documentos
        const files = (data.files_uploaded ? ` Archivos subidos: ${data.files_uploaded}.` : "")
          + (data.files_failed ? ` Archivos sin subir: ${data.files_failed} (${((data.file_errors || [])[0] || "").replace(/\.+$/, "")}).` : "");
        const bad = data.error || data.files_failed;
        toast({ title: bad ? "Sincronización con errores" : "Sincronizado", description: (data.error || `${data.created} nuevos, ${data.updated} actualizados, ${data.unchanged} sin cambios.`) + files, variant: bad ? "destructive" : "default" });
      }
      load();
    } catch (err) {
      toast({ title: "No se pudo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  if (!st) return null;
  const last = st.last_result;
  return (
    <div className="space-y-2" data-testid={ACADEMY.notionCard}>
      {!st.token_configured ? (
        <p className="text-sm text-[#8A6A2F]" data-testid={ACADEMY.notionStatus}>Falta la clave de Notion en el servidor (NOTION_TOKEN). La carga el responsable de la plataforma.</p>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid={ACADEMY.notionStatus}>
          {st.database_id ? `${st.synced_students} alumnos en Notion.` : "Cargá el link de la base de datos y guardá."}
          {st.last_sync_at ? ` Última sincronización: ${fmtDateTime(st.last_sync_at)}` : ""}
          {last ? (last.error ? ` · error: ${last.error}` : ` · ${last.created} nuevos, ${last.updated} actualizados`) : ""}
        </p>
      )}
      {st.token_configured && last?.files_failed > 0 && (
        <p className="text-sm text-[#A34B49]" data-testid={ACADEMY.notionFilesError}>
          Archivos que no se pudieron subir a Notion ({last.files_failed}): {(last.file_errors || []).map((x) => x.replace(/\.+$/, "")).join(" · ")}. Se reintentan con "Sincronizar ahora".
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => run("test")} disabled={!st.token_configured || !st.database_id || Boolean(busy)} data-testid={ACADEMY.notionTestButton}><PlugZap className="w-4 h-4" /> {busy === "test" ? "Probando..." : "Probar conexión"}</Button>
        <Button size="sm" variant="outline" onClick={() => run("sync")} disabled={!st.token_configured || !st.database_id || Boolean(busy)} data-testid={ACADEMY.notionSyncButton}><RefreshCw className={`w-4 h-4 ${busy === "sync" ? "animate-spin" : ""}`} /> Sincronizar ahora</Button>
      </div>
      <p className="text-xs text-muted-foreground">Se actualiza solo cada 5 minutos con los alumnos que cambiaron. En la columna "Documentos" van el contrato firmado y los documentos de cada alumno.</p>
    </div>
  );
}
